import request, { isPortalHybridEncryptionEnabled } from "@/utils/request"; // Axios instance with built-in encryption/decryption interceptor
import Apis from "./Apis";
import type FormCC from "@/interface/FormCC";
import type SendBack from "@/interface/FormCCSendBack";
import type FormBundlingValidation from "@/interface/FormBundlingValidation";
import type ResumeFindingsRequestModel from "@/interface/ResumeFindingsRequestModel";
export default class MasterService {
  // --- Basic Data Retrieval (GET requests are usually not encrypted) ---

  static getMasterData() {
    return request.get(`${Apis.FULL_URL}/master/cc/data`);
  }

  static getNationalityData() {
    return request.get(`${Apis.FULL_URL}/master/cc/nationality`);
  }

  static cardType() {
    return request.post(`${Apis.FULL_URL}/master/cardType/cc`, {
      requestModel: {},
    });
  }

  static getTNCPath(lang: string) {
    return request.get(`${Apis.FULL_URL}/master/cc/legal-doc/` + lang);
  }

  static getChannelData() {
    return request.get(`${Apis.FULL_URL}/biometric/channel`);
  }

  // --- Business Logic Requests (POST requests trigger encryption via interceptor) ---

  static async getBranchData(requestModel: object) {
    // Interceptor will automatically transform requestModel into { encryptedRequest: ... }
    return await request.post(`${Apis.FULL_URL}/master/branch/cc`, {
      requestModel,
    });
  }

  static async checkRecruiter(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/biometric/recruiter/validate`, {
      requestModel,
    });
  }

  static async biometricSubmitLogin(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/biometric/submit/login`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  static checkPostCode(postCode: string) {
    // If this API does not require encryption, ensure requestModel structure matches interceptor expectations
    return request.post(`${Apis.FULL_URL}/master/postcode/cc`, {
      requestModel: { postCode },
    });
  }

  static async basicInformationSubmit(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/cc/init`, { requestModel });
  }

  static ekycGenerate(requestModel: object, token: string) {
    return request.post(
      `${Apis.FULL_URL}/ekyc`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  static ekycCheckStatus(requestModel: object, token: string) {
    return request.post(
      `${Apis.FULL_URL}/ekyc/checkStatus`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  static async pageSubmit(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/submit`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  static async additionalInfomationSubmit(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/bundling/additionalInformation/submit`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  static async insuranceSubmit(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/bundling/insurance/submit`, {
      requestModel,
    });
  }

  // --- File Upload (Multipart requests are usually not encrypted, but include token) ---

  static async docSubmit(requestModel: FormCC, token: string) {
    const formData = new FormData();

    // Simplified file append logic (functionality unchanged)
    const appendFiles = (files: any[], key: string) => {
      files.forEach((fileFormData) => {
        for (const [name, value] of fileFormData.entries()) {
          if (name !== "" || value !== "") formData.append(key, value);
        }
      });
    };

    if (requestModel.ekyc.status === "COMPLETE") {
      appendFiles(
        requestModel.uploadDocument.proofIncome.file,
        "proofOfIncome",
      );
      appendFiles(
        requestModel.uploadDocument.proofIdentity.file,
        "proofOfIdentity",
      );
      appendFiles(requestModel.uploadDocument.proofJCKL.file, "proofOfJCKL");
    } else {
      appendFiles(
        requestModel.uploadDocument.proofIdentity.file,
        "proofOfIdentity",
      );
      appendFiles(
        requestModel.uploadDocument.proofIncome.file,
        "proofOfIncome",
      );
      appendFiles(
        requestModel.uploadDocument.additional.file,
        "proofOfDocument",
      );
      appendFiles(requestModel.uploadDocument.proofJCKL.file, "proofOfJCKL");
    }

    const headers: Record<string, string> = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };

    if (isPortalHybridEncryptionEnabled()) {
      const portalHybridMultipartInner = {
        webFormRequestId: requestModel.webFormRequestId,
        refNo: requestModel.refNo,
        imageFilePath: requestModel.imageFilePath,
        resume: requestModel.isDraft.toString(),
      };
      return request.post(`${Apis.FULL_URL}/cc/submit/document`, formData, {
        headers,
        portalHybridMultipartInner,
      } as any);
    }

    formData.append("webFormRequestId", requestModel.webFormRequestId);
    formData.append("refNo", requestModel.refNo);
    formData.append("imageFilePath", requestModel.imageFilePath);
    formData.append("resume", requestModel.isDraft.toString());

    return request.post(`${Apis.FULL_URL}/cc/submit/document`, formData, {
      headers,
    });
  }

  static async suppDocSubmit(requestModel: FormCC, token: string) {
    const formData = new FormData();
    requestModel.supplementaryUploadDocument.proofIdentity.file.forEach(
      (fileFormData) => {
        for (const entry of fileFormData.entries()) {
          const [name, value] = entry;
          if (name === "" && value === "") continue;
          formData.append("proofOfIdentity1", value);
        }
      },
    );

    const headers: Record<string, string> = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    };

    if (isPortalHybridEncryptionEnabled()) {
      const portalHybridMultipartInner = {
        webFormRequestId: requestModel.webFormRequestId,
        suppWebFormRequestId: requestModel.supplementary.webFormRequestId,
        imageFilePathUrl: requestModel.imageFilePath,
        resume: requestModel.isDraft.toString(),
        refNo: requestModel.refNo,
      };
      return request.post(
        `${Apis.FULL_URL}/cc/submit/supplementary/document`,
        formData,
        {
          headers,
          portalHybridMultipartInner,
        } as any,
      );
    }

    formData.append("webFormRequestId", requestModel.webFormRequestId);
    formData.append(
      "suppWebFormRequestId",
      requestModel.supplementary.webFormRequestId,
    );
    formData.append("imageFilePathUrl", requestModel.imageFilePath);
    formData.append("resume", requestModel.isDraft.toString());
    formData.append("refNo", requestModel.refNo);

    return request.post(
      `${Apis.FULL_URL}/cc/submit/supplementary/document`,
      formData,
      {
        headers,
      },
    );
  }

  // --- Other Business APIs (standardized structure) ---

  static async finalSubmit(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/submit/application`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
    );
  }

  static async validate(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/validate`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  static async cardSubmission(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/card/submit`,
      { requestModel },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }

  static async searchCIF(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/customer/inquiry`, {
      requestModel,
    });
  }

  static async getCIF(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/customer/getinfo`, {
      requestModel,
    });
  }

  static async getFile(requestModel: object) {
    return request.post(
      `${Apis.FULL_URL}/file/getFile`,
      { requestModel },
      { responseType: "arraybuffer" },
    );
  }

  static getDeclaration(name: string, type: string) {
    return request.post(
      `${Apis.FULL_URL}/pdfConvert`,
      { name, type },
      { headers: { pid: "70001" } },
    );
  }

  static preAssesment(requestModel: object, token: string) {
    return request.post(
      `${Apis.FULL_URL}/pa/rulesCheck`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static async suppCardSubmit(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/submit/supplementary`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static sendBackValidate(webFormRequestId: string) {
    return request.post(`${Apis.FULL_URL}/cc/sendback/validate`, {
      requestModel: { webFormRequestId },
    });
  }

  static async sendBackSubmit(requestModel: SendBack) {
    const formData = new FormData();
    requestModel.sendBackFile1.file.forEach((fileFormData) => {
      for (const entry of fileFormData.entries()) {
        const [, value] = entry;
        formData.append("sendBackDocument", value);
      }
    });

    const headers: Record<string, string> = {
      "Content-Type": "multipart/form-data",
    };

    if (isPortalHybridEncryptionEnabled()) {
      const portalHybridMultipartInner = {
        webFormRequestId: requestModel.webFormRequestId,
        imageFilePath: requestModel.imageFilePathUpload,
        imageFilePathUpload: requestModel.imageFilePath,
        refNo: requestModel.refNo,
      };
      return request.post(
        `${Apis.FULL_URL}/cc/sendback/document/submit`,
        formData,
        {
          headers,
          portalHybridMultipartInner,
        } as any,
      );
    }

    formData.append("webFormRequestId", requestModel.webFormRequestId);
    formData.append("imageFilePath", requestModel.imageFilePathUpload);
    formData.append("imageFilePathUpload", requestModel.imageFilePath);
    formData.append("refNo", requestModel.refNo);

    return request.post(
      `${Apis.FULL_URL}/cc/sendback/document/submit`,
      formData,
      {
        headers,
      },
    );
  }

  static async branchCode(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/master/branch/cc`, {
      requestModel,
    });
  }

  static cardValidate(requestModel: object) {
    return request.post(`${Apis.FULL_URL}/master/card/validate`, {
      requestModel,
    });
  }

  static preAssesmentValidate(requestModel: object, token: string) {
    return request.post(
      `${Apis.FULL_URL}/pa/validate`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static async editValidate(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/editValidate`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static async editEmploymentValidate(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/editValidate/Employment`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static async editSupplementaryValidate(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/editValidate/Supplementary`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static removeEKYC(requestModel: object, token: string) {
    return request.post(
      `${Apis.FULL_URL}/ekyc/removeEKYC`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static linkValidate(bdForm: FormBundlingValidation, webFormId: string) {
    return request.post(`${Apis.FULL_URL}/cc/validate/link`, {
      requestModel: bdForm,
      webformId: webFormId,
    });
  }

  static apiTest() {
    return request.post(`${Apis.FULL_URL}/cc/backend-route`, {
      requestModel: {},
    });
  }

  static async linkValidation(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/mc/linkCheck`, {
      requestModel,
    });
  }

  static async pfTimeout(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/bundling/timeout`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  /** POST body `{ requestModel }` binds to backend `ResumeFindingsRequest`. */
  static fetchRecords(requestModel: ResumeFindingsRequestModel) {
    return request.post(`${Apis.FULL_URL}/resume/fetchRecords`, {
      requestModel,
    });
  }

  static staffFetchRecords(requestModel: ResumeFindingsRequestModel) {
    return request.post(`${Apis.FULL_URL}/resume/staff/fetchRecords`, {
      requestModel,
    });
  }

  static sendBackDocSubmission(requestModel: ResumeFindingsRequestModel) {
    return request.post(`${Apis.FULL_URL}/resume/sendback/fetchRecords`, {
      requestModel,
    });
  }

  static generateToken(requestModel: object) {
    return request.post(`${Apis.FULL_URL}/jwt/token/generate`, {
      requestModel,
    });
  }

  static extendToken(requestModel: object) {
    return request.post(`${Apis.FULL_URL}/jwt/token/extend`, { requestModel });
  }

  static checkBranch(requestModel: object) {
    return request.post(`${Apis.FULL_URL}/resume/branchCheck`, {
      requestModel,
    });
  }

  static async dropOff(requestModel: object, token: string) {
    return await request.post(
      `${Apis.FULL_URL}/cc/dropOff`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static docCheck(requestModel: object, token: string) {
    return request.post(
      `${Apis.FULL_URL}/resume/doc/check`,
      { requestModel },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  static async productFlow(requestModel: object) {
    return await request.post(`${Apis.FULL_URL}/pm/getFlow`, {
      requestModel,
    });
  }
}
